import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Tasks } from '@air/api';
import { type SaveAssetTransformationResultAs } from '@air/api/types';

import { type AssetTransformTaskArgs, type AssetTransformType } from '~/components/EditMode/types';
import { centralizedClipWorkspaceIdSelector } from '~/store/centralizedClip/selectors';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

export const TRANSFORM_TYPE = 'clipVideo' satisfies AssetTransformType;

type ClipVideoArgs = AssetTransformTaskArgs<typeof TRANSFORM_TYPE>;

export type CreateVideoClipTaskParams = Pick<ClipVideoArgs, 'assetVersionId' | 'options'> & {
  saveResultAs: SaveAssetTransformationResultAs;
};

export const useCreateVideoClipTask = () => {
  const { createTask } = useCreateTask();
  const workspaceId = useSelector(centralizedClipWorkspaceIdSelector);

  const createVideoClipTask = useCallback(
    async (params: CreateVideoClipTaskParams) => {
      const { assetVersionId, options, saveResultAs } = params;

      const result = await createTask({
        task: {
          metadata: {
            transformType: TRANSFORM_TYPE,
            assetVersionId,
            options,
            saveResultAs,
          },
          type: 'AssetTransformation',
        },
        createTask: () =>
          Tasks.createTask({
            workspaceId,
            task: {
              type: 'AssetTransformer',
              args: {
                transformType: TRANSFORM_TYPE,
                assetVersionId,
                options,
                saveResultAs,
              },
            },
          }),
      });

      if (result.error) {
        throw result.error;
      }

      return result;
    },
    [createTask, workspaceId],
  );

  return { createVideoClipTask };
};
